// src/AppShell.js
// Layout wrapper dark JKL — sidebar + navbar + content area
// Usato solo per utenti autenticati (freelance per ora)

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchJson } from './api';
import { useTranslation } from 'react-i18next';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-deep: #0a0a0f;
    --bg-card: #111118;
    --bg-elevated: #18181f;
    --bg-hover: #1e1e28;
    --border: rgba(255,255,255,0.07);
    --border-accent: rgba(212,165,80,0.3);
    --gold: #d4a550;
    --gold-light: #f0c878;
    --gold-dim: rgba(212,165,80,0.12);
    --text-primary: #f0ece4;
    --text-secondary: #8a8a9a;
    --text-muted: #4a4a5a;
    --green: #4ade80;
    --red: #f87171;
    --radius: 12px;
  }

  html, body, #root {

    background: var(--bg-deep);
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
  }

  body::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 9999;
    opacity: 0.4;
  }

  /* ── NAVBAR ── */
  .jkl-navbar {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 200;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px 0 0;
    background: rgba(10,10,15,0.95);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border);
  }

  .jkl-nav-logo {
    width: 220px;
    height: 56px;
    display: flex;
    align-items: center;
    padding: 0 20px;
    border-right: 1px solid var(--border);
    flex-shrink: 0;
    gap: 10px;
    text-decoration: none;
  }

  .jkl-logo-img {
    height: 28px;
    width: auto;
    filter: brightness(0) invert(1);
  }

  .jkl-wordmark {
    font-family: 'Kanit', sans-serif;
    font-size: 20px;
    font-weight: 800;
    letter-spacing: 0.15em;
    color: var(--text-primary);
    text-transform: uppercase;
  }

  .jkl-nav-badge {
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    color: var(--gold);
    background: var(--gold-dim);
    border: 1px solid var(--border-accent);
    padding: 3px 10px;
    border-radius: 20px;
  }


  /* ── MOBILE SIDEBAR ── */
  .jkl-hamburger {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: var(--text-primary);
    font-size: 22px;
    line-height: 1;
  }
  .jkl-sidebar-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.6);
    z-index: 99;
  }
  @media (max-width: 768px) {
    .jkl-hamburger { display: flex; align-items: center; }
    .jkl-nav-logo { width: auto; border-right: none; }
    .jkl-wordmark { font-size: 17px; }
    .jkl-sidebar {
      transform: translateX(-100%);
      transition: transform 0.25s ease;
      z-index: 200;
      width: 260px;
    }
    .jkl-sidebar.open { transform: translateX(0); }
    .jkl-sidebar-overlay.open { display: block; }
    .jkl-content { margin-left: 0 !important; }
  }
  /* ── SIDEBAR ── */
  .jkl-sidebar {
    position: fixed;
    top: 56px;
    left: 0;
    bottom: 0;
    width: 220px;
    background: var(--bg-card);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    z-index: 100;
    overflow-y: hidden;
  }

  .jkl-sidebar-section {
    padding: 10px 12px 6px;
  }

  .jkl-sidebar-label {
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    color: var(--text-muted);
    padding: 0 12px 8px;
    display: block;
  }

  .jkl-nav-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s;
    font-family: 'Inter', sans-serif;
    font-size: 13.5px;
    font-weight: 400;
    color: var(--text-secondary);
    border: 1px solid transparent;
    margin-bottom: 1px;
    user-select: none;
  }
  .jkl-nav-item:hover { background: var(--bg-hover); color: var(--text-primary); }
  .jkl-nav-item.active {
    background: var(--gold-dim);
    color: var(--gold);
    border-color: var(--border-accent);
  }
  .jkl-nav-item.danger { color: var(--red); }
  .jkl-nav-item.danger:hover { background: rgba(248,113,113,0.08); color: var(--red); }

  .jkl-nav-item-left { display: flex; align-items: center; gap: 10px; }
  .jkl-nav-icon { font-size: 14px; width: 18px; text-align: center; flex-shrink: 0; }

  .jkl-msg-badge {
    background: var(--red);
    color: #fff;
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }

  .jkl-sidebar-divider {
    height: 1px;
    background: var(--border);
    margin: 8px 16px;
  }

  /* Profile in sidebar bottom */
  .jkl-sidebar-profile {
    margin-top: auto;
    padding: 12px;
    border-top: 1px solid var(--border);
  }

  .jkl-profile-inner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    cursor: pointer;
    transition: all 0.2s;
  }
  .jkl-profile-inner:hover { border-color: var(--border-accent); }

  .jkl-avatar-small {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: linear-gradient(135deg, #d4a550, #8b5e1a);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Kanit', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
    overflow: hidden;
  }

  .jkl-avatar-small img {
    width: 100%; height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  .jkl-profile-name {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .jkl-profile-role {
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 130px;
  }

  /* ── TRIAL BANNER ── */
  .jkl-trial-banner {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 10px 24px;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 500;
    border-bottom: 1px solid rgba(212,165,80,0.3);
    background: rgba(212,165,80,0.08);
    color: #f0ece4;
  }
  .jkl-trial-banner.urgent {
    background: rgba(248,113,113,0.10);
    border-bottom-color: rgba(248,113,113,0.35);
    color: #fecaca;
  }
  .jkl-trial-days {
    font-family: 'Kanit', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: var(--gold);
  }
  .jkl-trial-banner.urgent .jkl-trial-days {
    color: #f87171;
  }

  /* ── SUBSCRIPTION BANNER ── */
  .jkl-subscription-banner {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 10px 24px;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 500;
    border-bottom: 1px solid rgba(96,165,250,0.3);
    background: rgba(96,165,250,0.07);
    color: #e0eeff;
  }
  .jkl-subscription-days {
    font-family: 'Kanit', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: #60a5fa;
  }

  /* ── CONTENT ── */
  .jkl-content {
    margin-top: 56px;
    margin-left: 220px;
    height: calc(100vh - 56px);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
`;

// Voci di navigazione
function buildNavItems(userType, user) {
  const items = [
    { id: 'dashboard', icon: '◈', labelKey: 'shell.nav_dashboard', path: '/' },
    { id: 'messages',  icon: '◻', labelKey: 'shell.nav_messages',  path: '/messages' },
    { id: 'search',    icon: '◎', labelKey: 'shell.nav_search', path: '/cerca' },
  ];
  items.push({ id: 'eventi', icon: '◎', labelKey: 'shell.nav_events', path: '/eventi' });
  if (userType !== 'production_house') items.push({ id: 'piano-di-post', icon: '🎬', labelKey: 'shell.nav_postplan', path: '/piano-di-post' });
  if (userType === 'production_house') {
    items.push({ id: 'notifiche', icon: '🔔', labelKey: 'shell.nav_notifications', path: '/notifiche' });
    items.push({ id: 'squadre',   icon: '◉',  labelKey: 'shell.nav_squads',       path: '/squadre' });
    items.push({ id: 'radar',     icon: '📡', labelKey: 'shell.nav_radar',         path: '/radar' });
    items.push({ id: 'preferiti', icon: '★',  labelKey: 'shell.nav_favorites',     path: '/preferiti' });
    if (user?.company_role === 'supervisor') {
      items.push({ id: 'supervisor-alerts', icon: '📢', labelKey: 'shell.nav_alerts', path: '/supervisor/alerts' });
      items.push({ id: 'coordinatori', icon: '👥', labelKey: 'shell.nav_coordinators', path: '/supervisor/coordinatori' });
    }
  }
  return items;
}

export default function AppShell({ user, trialDaysLeft, subscriptionDaysLeft, onLogout, onEditProfile, onCloseEdit, onClearSelection, isEditProfileOpen, children }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const switchLang = (l) => i18n.changeLanguage(l);
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Persisti user_type in localStorage per evitare flash al reload
  useEffect(() => {
    if (user?.user_type) localStorage.setItem('jkl_user_type', user.user_type);
  }, [user?.user_type]);

  // Polling notifiche alert disponibilità (solo coordinator)
  const fetchNotifications = useCallback(async () => {
    if ((user?.user_type || localStorage.getItem('jkl_user_type')) !== 'production_house') return;
    try {
      const data = await fetchJson('alertsjobs/notifications');
      const unread = Array.isArray(data) ? data.filter(n => !n.read).length : 0;
      setNotifCount(unread);
    } catch { /* silenzioso */ }
  }, [user?.user_type]);

  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, 30000);
    return () => clearInterval(t);
  }, [fetchNotifications]);

  // Polling unread messages
  const fetchUnread = useCallback(async () => {
    try {
      const res = await fetchJson('messages/inbox');
      const items = Array.isArray(res?.items) ? res.items : [];
      const total = items.reduce((acc, c) => acc + Number(c.unread_count || 0), 0);
      setUnreadCount(total);
    } catch { /* silenzioso */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 15000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  const effectiveUserType = user?.user_type || localStorage.getItem('jkl_user_type') || '';
  const navItems = buildNavItems(effectiveUserType, user);

  // Determina voce attiva da URL
  const activeId = (() => {
    const p = location.pathname;
    if (isEditProfileOpen) return '';
    if (p === '/' || p === '') return 'dashboard';
    if (p.startsWith('/messages')) return 'messages';
    if (p.startsWith('/cerca')) return 'search';
    if (p.startsWith('/preferiti')) return 'preferiti';
    if (p.startsWith('/notifiche')) return 'notifiche';
    if (p.startsWith('/radar')) return 'radar';
    if (p.startsWith('/squadre')) return 'squadre';
    if (p.startsWith('/eventi')) return 'eventi';
    if (p.startsWith('/piani')) return 'piani';
    if (p.startsWith('/productions')) return 'productions';
    if (p.startsWith('/postplans')) return 'postplans';
    if (p.startsWith('/profile')) return 'profile';
    if (p.startsWith('/premium')) return 'premium';
    if (p.startsWith('/supervisor/alerts')) return 'supervisor-alerts';
    if (p.startsWith('/supervisor/coordinatori')) return 'coordinatori';
    return '';
  })();



  // Iniziali avatar
  const initials = [user?.nome, user?.cognome]
    .filter(Boolean)
    .map(s => s[0].toUpperCase())
    .join('') || user?.email?.[0]?.toUpperCase() || '?';

  const fullName = [user?.nome, user?.cognome].filter(Boolean).join(' ') || user?.email || 'Utente';

  return (
    <>
      <style>{STYLES}</style>

      {/* NAVBAR */}
      <nav className="jkl-navbar">
        <button className="jkl-hamburger" onClick={() => setSidebarOpen(v => !v)}>☰</button>
        <div className="jkl-nav-logo" style={{display:"flex",alignItems:"center"}}>
          <svg width="80" height="28" viewBox="0 0 80 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="0" width="22" height="22" rx="3.5" fill="rgba(212,165,80,0.12)" stroke="rgba(212,165,80,0.5)" strokeWidth="1"/>
            <text x="11" y="15.5" textAnchor="middle" fill="#d4a550" fontSize="13" fontWeight="700" fontFamily="Kanit,Inter,sans-serif">J</text>
            <rect x="29" y="0" width="22" height="22" rx="3.5" fill="rgba(212,165,80,0.12)" stroke="rgba(212,165,80,0.5)" strokeWidth="1"/>
            <text x="40" y="15.5" textAnchor="middle" fill="#d4a550" fontSize="13" fontWeight="700" fontFamily="Kanit,Inter,sans-serif">K</text>
            <rect x="58" y="0" width="22" height="22" rx="3.5" fill="rgba(212,165,80,0.12)" stroke="rgba(212,165,80,0.5)" strokeWidth="1"/>
            <text x="69" y="15.5" textAnchor="middle" fill="#d4a550" fontSize="13" fontWeight="700" fontFamily="Kanit,Inter,sans-serif">L</text>
          </svg>
        </div>
        <span className="jkl-nav-badge">
          {user?.user_type === 'production_house' ? (user?.company_role === 'supervisor' ? t('shell.badge_supervisor') : t('shell.badge_coordinator')) : t('shell.badge_freelance')}
        </span>
        <div style={{display:'flex',alignItems:'center',gap:6,marginLeft:'auto',paddingRight:8}}>
          <button onClick={()=>switchLang('it')} style={{background:'none',border:'none',cursor:'pointer',fontFamily:'Space Mono,monospace',fontSize:10,letterSpacing:'0.1em',textTransform:'uppercase',color:lang==='it'?'#d4a550':'#4a4a5a',padding:'2px 4px'}}>IT</button>
          <span style={{color:'#2a2a38',fontSize:10}}>|</span>
          <button onClick={()=>switchLang('en')} style={{background:'none',border:'none',cursor:'pointer',fontFamily:'Space Mono,monospace',fontSize:10,letterSpacing:'0.1em',textTransform:'uppercase',color:lang==='en'?'#d4a550':'#4a4a5a',padding:'2px 4px'}}>EN</button>
        </div>
      </nav>

      {/* SIDEBAR */}
      <div className={`jkl-sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <aside className={`jkl-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="jkl-sidebar-section">
          <span className="jkl-sidebar-label">{t('shell.menu')}</span>

          {navItems.map(item => (
            <Link
              key={item.id}
              to={item.path}
              className={`jkl-nav-item ${activeId === item.id ? 'active' : ''}`}
              onClick={() => { setSidebarOpen(false); onCloseEdit && onCloseEdit(); onClearSelection && onClearSelection(); }}
              style={{ textDecoration: 'none' }}
            >
              <div className="jkl-nav-item-left">
                <span className="jkl-nav-icon">{item.icon}</span>
                {t(item.labelKey)}
              </div>
              {item.id === 'messages' && unreadCount > 0 && (
                <span className="jkl-msg-badge">{unreadCount}</span>
              )}
              {item.id === 'notifiche' && notifCount > 0 && (
                <span className="jkl-msg-badge" style={{background:'#d4a550'}}>{notifCount}</span>
              )}
            </Link>
          ))}
        </div>

        <div className="jkl-sidebar-divider" />

        <div className="jkl-sidebar-section">
          <span className="jkl-sidebar-label">{t('shell.account')}</span>

<div
            className="jkl-nav-item danger"
            onClick={onLogout}
          >
            <div className="jkl-nav-item-left">
              <span className="jkl-nav-icon">↩</span>
              {t('shell.nav_logout')}
            </div>
          </div>
        </div>

        <div className="jkl-sidebar-divider" />
        <div className="jkl-sidebar-section">
          {effectiveUserType === 'production_house' && (
            <Link
              to="/premium"
              className={`jkl-nav-item ${activeId === 'premium' ? 'active' : ''}`}
              onClick={() => { setSidebarOpen(false); onCloseEdit && onCloseEdit(); onClearSelection && onClearSelection(); }}
              style={{ textDecoration:'none', color:'var(--gold)', borderColor:'rgba(212,165,80,0.2)', background:'rgba(212,165,80,0.06)' }}
            >
              <div className="jkl-nav-item-left">
                <span className="jkl-nav-icon">♛</span>
                {t('shell.nav_premium')}
              </div>
            </Link>
          )}
          <Link
              to="/contatti"
              className="jkl-nav-item"
              onClick={() => { setSidebarOpen(false); }}
              style={{ textDecoration: 'none' }}
            >
              <div className="jkl-nav-item-left">
                <span className="jkl-nav-icon">◎</span>
                {t('shell.nav_support')}
              </div>
            </Link>
        </div>

        {/* Profile card in fondo */}
        <div className="jkl-sidebar-profile">
          <Link to="/profile/edit" className="jkl-profile-inner" onClick={() => { setSidebarOpen(false); onCloseEdit && onCloseEdit(); onClearSelection && onClearSelection(); }} style={{ textDecoration: "none" }}>
            <div className="jkl-avatar-small">
              {user?.foto_url
                ? <img src={user.foto_url} alt={fullName} />
                : initials
              }
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="jkl-profile-name">{fullName}</div>
              <div className="jkl-profile-role">{user?.user_type === 'production_house' ? (user?.azienda || (user?.company_role === 'supervisor' ? t('shell.role_supervisor') : t('shell.role_coordinator'))) : (user?.ruolo || t('shell.role_freelance'))}</div>
            </div>
          </Link>
        </div>
      </aside>

      {/* CONTENT */}
      <main className="jkl-content">
        {user?.user_type === 'production_house' && (
          subscriptionDaysLeft !== null && subscriptionDaysLeft !== undefined && subscriptionDaysLeft <= 30
            ? (
              <div className="jkl-subscription-banner">
                <span>ℹ</span>
                <span>
                  {subscriptionDaysLeft === 1
                    ? t('shell.subscription_expiring_one')
                    : t('shell.subscription_expiring_other', { count: subscriptionDaysLeft })
                  }
                </span>
              </div>
            )
            : trialDaysLeft !== null && trialDaysLeft !== undefined
              ? (
                <div className={`jkl-trial-banner${trialDaysLeft <= 7 ? ' urgent' : ''}`}>
                  <span>⏳</span>
                  <span dangerouslySetInnerHTML={{ __html: t('shell.trial_intro') }} />
                  {trialDaysLeft === 0
                    ? <span>{t('shell.trial_expired')}</span>
                    : <span>{t('shell.trial_remaining_pre')} <span className="jkl-trial-days">{trialDaysLeft} {trialDaysLeft === 1 ? t('shell.trial_day') : t('shell.trial_days')}</span> {t('shell.trial_remaining_post')}</span>
                  }
                </div>
              )
              : null
        )}
        {children}
      </main>
    </>
  );
}
